import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Shield, AlertTriangle, CheckCircle2, MailWarning, ExternalLink, Globe } from "lucide-react";
import Layout from "../components/Layout";

const HEALTH_COLOR = {
  excellent: "hsl(var(--success))",
  good: "hsl(var(--success))",
  watch: "hsl(var(--warning))",
  at_risk: "hsl(var(--destructive))",
};

const HEALTH_LABEL = {
  excellent: "Excellent",
  good: "Good",
  watch: "Watch",
  at_risk: "At risk",
};

export default function Deliverability() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/deliverability/overview");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load deliverability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Layout
      title="Inbox health"
      subtitle="Domains & deliverability — pulled from Instantly. Spot a domain going cold before it kills a campaign."
      actions={
        <div className="flex items-center gap-3">
          {data?.synced_at && (
            <span className="text-xs text-muted-foreground font-mono hidden sm:block" data-testid="deliverability-synced-at">
              synced {new Date(data.synced_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <Button variant="outline" onClick={load} disabled={loading} data-testid="deliverability-refresh" style={{ borderRadius: 2 }}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Refresh
          </Button>
        </div>
      }
    >
    <div data-testid="deliverability-page">

      {!loading && data && !data.connected && (
        <div className="bg-card border border-border p-6 mb-6" style={{ borderRadius: 2 }} data-testid="deliverability-not-connected">
          <div className="flex items-start gap-3">
            <MailWarning className="h-5 w-5 text-[hsl(var(--warning))] shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-medium mb-1">Instantly not connected</div>
              <div className="text-sm text-muted-foreground">
                Paste your Instantly API V2 key in <a className="underline" href="/settings">Settings → Instantly</a> to see warmed mailboxes, deliverability scores, and per-domain reply rates here.
              </div>
            </div>
          </div>
        </div>
      )}

      {data && (data.totals?.mailboxes > 0 || data.totals?.sent_30d > 0) && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <Stat label="Mailboxes warmed" value={data.totals.mailboxes} />
          <Stat label="Domains" value={data.totals.domains} />
          <Stat label="Daily send capacity" value={data.totals.daily_capacity} />
          <Stat label="Avg warm-up" value={`${data.totals.avg_warmup}%`} accent={data.totals.avg_warmup >= 75} />
          <Stat label="30-day reply rate" value={`${data.totals.reply_rate}%`} sub={`${data.totals.replies_30d}/${data.totals.sent_30d}`} accent={data.totals.reply_rate >= 3} />
        </div>
      )}

      {/* Per-domain rollup */}
      {data && data.domains.length > 0 && (
        <div className="mb-8">
          <div className="overline mb-3">By domain · {data.domains.length}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {data.domains.map((d) => (
              <DomainCard key={d.domain} d={d} />
            ))}
          </div>
        </div>
      )}

      {/* Mailbox table */}
      {data && data.mailboxes.length > 0 && (
        <div>
          <div className="overline mb-3">Mailboxes · {data.mailboxes.length}</div>
          <div className="bg-card border border-border" style={{ borderRadius: 2 }} data-testid="deliverability-mailbox-table">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/30">
                <tr className="text-left">
                  <Th>Mailbox</Th>
                  <Th>Domain</Th>
                  <Th>Warm-up</Th>
                  <Th>Today</Th>
                  <Th>Daily cap</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {data.mailboxes.map((m) => (
                  <tr key={m.email} className="border-b border-border/50 hover:bg-secondary/20" data-testid={`deliverability-mailbox-${m.email}`}>
                    <Td className="font-mono text-xs">{m.email}</Td>
                    <Td className="text-xs text-muted-foreground">{m.domain}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-xs w-8">{m.warmup_score}%</div>
                        <div className="h-1.5 w-24 bg-secondary overflow-hidden" style={{ borderRadius: 1 }}>
                          <div style={{ width: `${m.warmup_score}%`, background: HEALTH_COLOR[m.health], height: "100%" }} />
                        </div>
                      </div>
                    </Td>
                    <Td className="font-mono text-xs">{m.daily_sent}</Td>
                    <Td className="font-mono text-xs">{m.daily_limit || "—"}</Td>
                    <Td>
                      <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 border" style={{ borderColor: HEALTH_COLOR[m.health], color: HEALTH_COLOR[m.health], borderRadius: 2 }}>
                        {HEALTH_LABEL[m.health]}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && data && data.mailboxes.length === 0 && data.connected && (
        <div className="bg-card border border-border p-6 text-sm text-muted-foreground" style={{ borderRadius: 2 }} data-testid="deliverability-empty">
          No warmed mailboxes yet in Instantly. Add at least one (Settings → Instantly card → "Connect" or open <a className="underline" href="https://app.instantly.ai/app/accounts" target="_blank" rel="noreferrer">app.instantly.ai/app/accounts <ExternalLink className="inline h-3 w-3" /></a>) and toggle warmup ON. Come back here in 24–48h to see scores climb.
          {data.reasons?.length > 0 && (
            <ul className="mt-3 list-disc ml-5 text-xs">
              {data.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
        </div>
      )}

      <DeliverabilityHowTo />
    </div>
    </Layout>
  );
}

function DomainCard({ d }) {
  const lowReply = d.sent_30d >= 30 && d.reply_rate < 1.5;
  return (
    <div className="bg-card border border-border p-4" style={{ borderRadius: 2 }} data-testid={`deliverability-domain-${d.domain}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
            <div className="font-mono text-sm">{d.domain}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{d.mailbox_count} mailbox{d.mailbox_count === 1 ? "" : "es"} · {d.total_daily_limit} daily cap</div>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 border shrink-0" style={{ borderColor: HEALTH_COLOR[d.health], color: HEALTH_COLOR[d.health], borderRadius: 2 }}>
          {HEALTH_LABEL[d.health]}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="overline">Avg warm-up</div>
          <div className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>{d.avg_warmup}%</div>
        </div>
        <div>
          <div className="overline">Sent · 30d</div>
          <div className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>{d.sent_30d}</div>
        </div>
        <div>
          <div className="overline">Reply rate</div>
          <div className="font-serif text-2xl tracking-tight flex items-baseline gap-1" style={{ fontWeight: 400 }}>
            {d.reply_rate}%
            <span className="text-xs text-muted-foreground font-mono">{d.replies_30d}/{d.sent_30d}</span>
          </div>
        </div>
      </div>
      {(d.health === "watch" || d.health === "at_risk" || lowReply) && (
        <div className="mt-3 pt-3 border-t border-border flex items-start gap-2 text-xs text-muted-foreground">
          <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))] mt-0.5 shrink-0" strokeWidth={1.5} />
          <div>
            {d.health === "at_risk" && <strong className="text-foreground">Pause sends</strong>}
            {d.health === "watch" && <strong className="text-foreground">Cut volume 50%</strong>}
            {d.health === "good" && lowReply && <strong className="text-foreground">Tighten ICP — reply rate too low</strong>}
            {" "}— consider extending warmup another 7 days, lowering daily cap, or rotating in a fresh domain.
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="bg-card border border-border p-4" style={{ borderRadius: 2 }} data-testid={`deliverability-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="overline">{label}</div>
      <div className="font-serif text-3xl tracking-tight mt-1" style={{ fontWeight: 400, color: accent ? "hsl(var(--success))" : undefined }}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-mono mt-0.5">{sub}</div>}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-mono font-normal">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

function DeliverabilityHowTo() {
  return (
    <div className="mt-10 bg-card border border-border p-6" style={{ borderRadius: 2 }}>
      <div className="flex items-start gap-3">
        <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="font-serif text-xl tracking-tight mb-2" style={{ fontWeight: 450 }}>How to keep these scores green</div>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal ml-4">
            <li>Buy 2–3 cousin domains (e.g. <span className="font-mono text-foreground">era-cfo.com</span>, <span className="font-mono text-foreground">getera.io</span>). Cloudflare Registrar for <span className="font-mono">.com/.io</span>, Crazy Domains for <span className="font-mono">.com.au</span>.</li>
            <li>Create 2 mailboxes per domain in Google Workspace. Add each to Instantly → wizard generates SPF/DKIM/DMARC records to paste into Cloudflare DNS.</li>
            <li>Toggle warm-up ON for each mailbox. Wait 3–4 weeks for scores to climb above 90%.</li>
            <li>Go live: in <a className="underline text-foreground" href="/settings">Settings</a> point sender provider to Instantly + pick the campaign that hosts your warmed mailboxes.</li>
            <li>Watch this page. If a domain drops below 75% warm-up or its reply rate falls under 1.5% on 30+ sends — pause it and rotate in a fresh one.</li>
          </ol>
          <div className="text-xs text-muted-foreground mt-4 italic flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 text-[hsl(var(--success))]" strokeWidth={1.5} /> A reply rate of 3–5% on cold CFO outreach is excellent. Below 1.5% usually means ICP is too broad, not deliverability.
          </div>
        </div>
      </div>
    </div>
  );
}
